// Pieces shared by the attendance list and the mark-attendance form.
// They live here rather than in either page because ListAttendance
// imports AddAttendance -- putting them in ListAttendance and importing
// back would close a cycle.
import React from "react";

// Initials for the avatar: first letter of the name and of the surname.
// A single-word name gives one letter rather than two of the same.
export const initialsOf = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Six tints, picked from the name so a given student keeps the same one
// between openings rather than shifting with row order.
export const avatarTone = (name) => {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 6;
  return h;
};

// A two-slice donut. Drawn with one circle and stroke-dasharray rather
// than arc paths: the circumference is the whole length, so the present
// share is just a fraction of it and the two can never disagree.
export const AttendancePie = ({ present, absent }) => {
  const total = present + absent;
  const r = 54;
  const circumference = 2 * Math.PI * r;
  const presentLen = total > 0 ? (present / total) * circumference : 0;
  const pct = total > 0 ? Math.round((present / total) * 100) : 0;

  return (
    <div className="pgm-pie">
      <svg
        className="pgm-pie-svg"
        viewBox="0 0 140 140"
        role="img"
        aria-label={`${present} present and ${absent} absent of ${total} students`}
      >
        {/* The absent share is the full ring; the present share is drawn
            over it, so the two always add to the whole. */}
        <circle cx="70" cy="70" r={r} className="pgm-pie-track" />
        <circle
          cx="70"
          cy="70"
          r={r}
          className="pgm-pie-fill"
          strokeDasharray={`${presentLen} ${circumference - presentLen}`}
          transform="rotate(-90 70 70)"
        />
        <text x="70" y="65" className="pgm-pie-figure">
          {pct}%
        </text>
        <text x="70" y="86" className="pgm-pie-caption">
          present
        </text>
      </svg>

      <dl className="pgm-pie-key">
        <div className="pgm-pie-item">
          <dt>
            <span className="pgm-pie-dot pgm-pie-dot--present" />
            Present
          </dt>
          <dd>{present}</dd>
        </div>
        <div className="pgm-pie-item">
          <dt>
            <span className="pgm-pie-dot pgm-pie-dot--absent" />
            Absent
          </dt>
          <dd>{absent}</dd>
        </div>
        <div className="pgm-pie-item pgm-pie-item--total">
          <dt>Total students</dt>
          <dd>{total}</dd>
        </div>
      </dl>
    </div>
  );
};

/* The 48-hour window used to be recomputed here for the list and the
   form. It is the API's answer now -- GET /attendances/:id returns
   `editability` for whoever asked, which also settles whether the record
   is theirs to edit at all, something a date in the browser cannot know.
   Nothing mirrors the rule any more, so nothing can drift from it. */
